// src/screens/MyLeaguesScreen.js
// "League" nav hub — every live league you're part of (solo entrant, team
// captain, or squad member), your concurrent-slot usage, and league creation.
// Slots: 5 free, then permanent Crit unlocks at climbing prices (mirrors
// functions/constants/leagueEconomy.js; the server is authoritative).
import React, { useEffect, useState } from 'react';
import {
  View, Text, SafeAreaView, ScrollView, StyleSheet,
  TouchableOpacity, ActivityIndicator,
} from 'react-native';
import { onSnapshot, collection, query, where, doc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../../firebaseConfig';

import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { useError } from '../components/ErrorContext';
import useCritsEnabled from '../hooks/useCritsEnabled';
import { GAME_PROFILES } from '../constants/gameData';

const GOLD = '#FCD000';
// Mirrors functions/constants/leagueEconomy.js LEAGUE_FREE_SLOTS / ladder.
const FREE_SLOTS = 5;
const SLOT_LADDER = [10, 25, 50, 100, 200];

const tsMs = (v) => (v?.toMillis ? v.toMillis() : (v?.seconds != null ? v.seconds * 1000 : NaN));
const fmtWhen = (ms) => {
  if (!Number.isFinite(ms)) return '';
  const d = new Date(ms);
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
};

// Is this uid part of the league in any capacity?
const isMemberOf = (lg, uid) => (lg.rosterUids || []).includes(uid)
  || Object.values(lg.roster || {}).some((info) =>
    (info?.lineupUids || []).includes(uid)
    || (info?.paidUids || []).includes(uid)
    || (info?.teamRosterUids || []).includes(uid)
    || (info?.squadUids || []).includes(uid));

export default function MyLeaguesScreen({ navigation }) {
  const { showError } = useError();
  const uid = auth.currentUser?.uid || null;
  // Evergreen step 2.3: when Crits are off the slot unlock is free (the
  // server prices it at 0) — the ladder still caps at 10 slots either way.
  const critsOn = useCritsEnabled();

  const [liveLeagues, setLiveLeagues] = useState(undefined);
  const [userData, setUserData] = useState(null);
  const [unlocking, setUnlocking] = useState(false);

  useEffect(() => {
    const qL = query(collection(db, 'leagues'), where('status', 'in', ['registration', 'active']));
    const unsub = onSnapshot(qL, (snap) => {
      setLiveLeagues(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, () => setLiveLeagues([]));
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!uid) return;
    const unsub = onSnapshot(doc(db, 'users', uid), (snap) => {
      setUserData(snap.exists() ? snap.data() : null);
    });
    return () => unsub();
  }, [uid]);

  const mine = (liveLeagues || [])
    .filter((lg) => uid && isMemberOf(lg, uid))
    .sort((a, b) => (tsMs(a.startAt) || 0) - (tsMs(b.startAt) || 0));

  // Slots count ENTRIES (you as solo player or team captain) — riding on a
  // captain's roster doesn't use one of yours.
  const slotsUsed = (liveLeagues || []).filter((lg) => (lg.rosterUids || []).includes(uid)).length;
  const purchased = Math.max(0, Math.floor(Number(userData?.leagueSlotsPurchased || 0)));
  const slotLimit = FREE_SLOTS + purchased;
  const nextPrice = SLOT_LADDER[purchased];

  const onUnlockSlot = async () => {
    if (typeof window !== 'undefined'
      && !window.confirm(`Unlock a permanent slot #${slotLimit + 1}${critsOn ? ` for ${nextPrice} Crits` : ''}? It frees up and gets reused every time a league of yours ends.`)) return;
    setUnlocking(true);
    try {
      await httpsCallable(getFunctions(undefined, 'us-central1'), 'purchaseLeagueSlot')({});
    } catch (err) {
      showError('Could not unlock slot', String(err?.message || '').replace(/^[a-z-]+:\s*/i, '') || 'Please try again.');
    } finally {
      setUnlocking(false);
    }
  };

  const roleFor = (lg) => {
    if (lg.createdByUid === uid) return 'Commissioner';
    if ((lg.rosterUids || []).includes(uid)) {
      return lg.isTeamLeague || Number(lg.teamSize || 1) > 1 ? 'Captain' : 'Player';
    }
    return 'Team member';
  };

  return (
    <SafeAreaView style={st.safe}>
      <Header navigation={navigation} />
      <ScrollView contentContainerStyle={st.container}>
        <Text style={st.title}>🏆 Your Leagues</Text>

        {/* Slot meter */}
        {!!uid && (
          <View style={st.slotBox}>
            <Text style={st.slotText}>
              League slots: <Text style={st.slotStrong}>{slotsUsed} of {slotLimit}</Text> in use
              {purchased > 0 ? ` (${FREE_SLOTS} free + ${purchased} unlocked)` : ''}
            </Text>
            <Text style={st.slotNote}>
              A slot is used per league you enter (or captain a team into) and frees up when it ends.
            </Text>
            {slotsUsed >= slotLimit && nextPrice != null && (
              <TouchableOpacity style={st.unlockBtn} disabled={unlocking} onPress={onUnlockSlot}>
                {unlocking ? <ActivityIndicator color="#000" /> : (
                  <Text style={st.unlockBtnText}>Unlock slot #{slotLimit + 1}{critsOn ? ` — ${nextPrice} Crits` : ''}</Text>
                )}
              </TouchableOpacity>
            )}
          </View>
        )}

        {liveLeagues === undefined ? (
          <ActivityIndicator color={GOLD} style={{ marginTop: 40 }} />
        ) : mine.length === 0 ? (
          <Text style={st.empty}>
            You're not in a league yet. Post your own below, or find one to join in the Lobby.
          </Text>
        ) : mine.map((lg) => {
          const gp = GAME_PROFILES[lg.game];
          return (
            <TouchableOpacity key={lg.id} style={st.card}
              onPress={() => navigation.navigate('LeagueDetail', { leagueId: lg.id })}>
              <View style={{ flex: 1 }}>
                <Text style={st.cardName} numberOfLines={1}>{lg.name}</Text>
                <Text style={st.cardMeta}>
                  {gp?.displayName || lg.game} · {roleFor(lg)}
                  {Number.isFinite(tsMs(lg.startAt)) ? ` · starts ${fmtWhen(tsMs(lg.startAt))}` : ''}
                </Text>
              </View>
              <View style={[st.statusChip, lg.status === 'active' && st.statusChipLive]}>
                <Text style={[st.statusChipText, lg.status === 'active' && st.statusChipTextLive]}>
                  {lg.status === 'active' ? 'RUNNING' : 'FILLING'}
                </Text>
              </View>
            </TouchableOpacity>
          );
        })}

        <TouchableOpacity style={st.createBtn} onPress={() => navigation.navigate('CreateLeague')}>
          <Text style={st.createBtnText}>+ Post a League</Text>
        </TouchableOpacity>
        <TouchableOpacity style={st.browseBtn} onPress={() => navigation.navigate('Lobby')}>
          <Text style={st.browseBtnText}>Browse open leagues in the Lobby</Text>
        </TouchableOpacity>
      </ScrollView>
      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

const st = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#0b0b0b' },
  container: { padding: 14, paddingBottom: 120, maxWidth: 760, width: '100%', alignSelf: 'center' },
  title: { color: '#fff', fontSize: 22, fontWeight: '900', marginBottom: 10 },
  slotBox: {
    backgroundColor: '#101010', borderLeftWidth: 3, borderLeftColor: GOLD,
    borderRadius: 8, padding: 10, marginBottom: 12,
  },
  slotText: { color: '#cfcfcf', fontSize: 13 },
  slotStrong: { color: GOLD, fontWeight: '900' },
  slotNote: { color: '#8a8a8a', fontSize: 11, marginTop: 4, lineHeight: 15 },
  unlockBtn: {
    backgroundColor: GOLD, borderRadius: 10, paddingVertical: 10,
    alignItems: 'center', marginTop: 10,
  },
  unlockBtnText: { color: '#000', fontSize: 13, fontWeight: '900' },
  empty: { color: '#8a8a8a', fontSize: 13, marginVertical: 20, lineHeight: 18 },
  card: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#0f0f0f', borderWidth: 1, borderColor: '#1a1a1a',
    borderRadius: 12, padding: 12, marginBottom: 8,
  },
  cardName: { color: '#fff', fontSize: 15, fontWeight: '800' },
  cardMeta: { color: '#8a8a8a', fontSize: 12, marginTop: 3 },
  statusChip: {
    borderWidth: 1, borderColor: '#2a2a2a', borderRadius: 8,
    paddingVertical: 4, paddingHorizontal: 8, marginLeft: 10,
  },
  statusChipLive: { borderColor: '#2ECC71' },
  statusChipText: { color: '#8a8a8a', fontSize: 10, fontWeight: '900', letterSpacing: 0.5 },
  statusChipTextLive: { color: '#2ECC71' },
  createBtn: {
    backgroundColor: GOLD, borderRadius: 12, paddingVertical: 14,
    alignItems: 'center', marginTop: 14,
  },
  createBtnText: { color: '#000', fontSize: 15, fontWeight: '900' },
  browseBtn: { alignItems: 'center', marginTop: 12 },
  browseBtnText: { color: '#4FC3F7', fontSize: 13, fontWeight: '800' },
});
